import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useWholesale } from '../contexts/WholesaleContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const WholesaleRegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useWholesale();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    company_name: '',
    contact_name: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    tax_id: '',
    notes: '',
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const { confirmPassword, ...submitData } = formData;
      await register(submitData);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="paper-texture min-h-screen py-12">
        <div className="max-w-md mx-auto px-6">
          <div className="bg-white border border-alt-bg p-8 soft-shadow text-center">
            <div className="text-6xl mb-4">✓</div>
            <h1 className="font-display text-3xl mb-4">Registration Submitted!</h1>
            <p className="text-[#8C8885] mb-6">
              Thank you for registering as a wholesale client. Your account is pending approval.
              We will contact you at <strong>{formData.email}</strong> once your account has been approved.
            </p>
            <Link to="/wholesale/login">
              <Button className="bg-cape-red hover:bg-red-800 text-white">
                Go to Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="paper-texture min-h-screen py-12">
      <div className="max-w-2xl mx-auto px-6">
        <div className="text-center mb-8">
          <h1 className="font-display text-4xl mb-4">Wholesale Registration</h1>
          <p className="text-[#8C8885]">Apply for a wholesale account to access wholesale pricing</p>
        </div>

        <div className="bg-white border border-alt-bg p-8 soft-shadow">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Company Information */}
            <div>
              <h2 className="font-display text-xl mb-4 pb-2 border-b border-alt-bg">Company Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="company_name" className="text-sm font-semibold uppercase tracking-wider mb-2">
                    Company Name *
                  </Label>
                  <Input
                    id="company_name"
                    name="company_name"
                    type="text"
                    required
                    value={formData.company_name}
                    onChange={handleInputChange}
                    className="w-full"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_name" className="text-sm font-semibold uppercase tracking-wider mb-2">
                    Contact Name *
                  </Label>
                  <Input
                    id="contact_name"
                    name="contact_name"
                    type="text"
                    required
                    value={formData.contact_name}
                    onChange={handleInputChange}
                    className="w-full"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-sm font-semibold uppercase tracking-wider mb-2">
                    Phone *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full"
                  />
                </div>
                <div>
                  <Label htmlFor="tax_id" className="text-sm font-semibold uppercase tracking-wider mb-2">
                    Tax ID / EIN (optional)
                  </Label>
                  <Input
                    id="tax_id"
                    name="tax_id"
                    type="text"
                    value={formData.tax_id}
                    onChange={handleInputChange}
                    className="w-full"
                    placeholder="XX-XXXXXXX"
                  />
                </div>
              </div>
            </div>

            {/* Address */}
            <div>
              <h2 className="font-display text-xl mb-4 pb-2 border-b border-alt-bg">Business Address</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="address" className="text-sm font-semibold uppercase tracking-wider mb-2">
                    Street Address *
                  </Label>
                  <Input
                    id="address"
                    name="address"
                    type="text"
                    required
                    value={formData.address}
                    onChange={handleInputChange}
                    className="w-full"
                  />
                </div>
                <div>
                  <Label htmlFor="city" className="text-sm font-semibold uppercase tracking-wider mb-2">
                    City *
                  </Label>
                  <Input
                    id="city"
                    name="city"
                    type="text"
                    required
                    value={formData.city}
                    onChange={handleInputChange}
                    className="w-full"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="state" className="text-sm font-semibold uppercase tracking-wider mb-2">
                      State *
                    </Label>
                    <Input
                      id="state"
                      name="state"
                      type="text"
                      required
                      value={formData.state}
                      onChange={handleInputChange}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Label htmlFor="zip_code" className="text-sm font-semibold uppercase tracking-wider mb-2">
                      ZIP *
                    </Label>
                    <Input
                      id="zip_code"
                      name="zip_code"
                      type="text"
                      required
                      value={formData.zip_code}
                      onChange={handleInputChange}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Account Credentials */}
            <div>
              <h2 className="font-display text-xl mb-4 pb-2 border-b border-alt-bg">Account Credentials</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="email" className="text-sm font-semibold uppercase tracking-wider mb-2">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full"
                  />
                </div>
                <div>
                  <Label htmlFor="password" className="text-sm font-semibold uppercase tracking-wider mb-2">
                    Password *
                  </Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    required
                    minLength={6}
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full"
                  />
                </div>
                <div>
                  <Label htmlFor="confirmPassword" className="text-sm font-semibold uppercase tracking-wider mb-2">
                    Confirm Password *
                  </Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    required
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="notes" className="text-sm font-semibold uppercase tracking-wider mb-2">
                Additional Notes (optional)
              </Label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                className="w-full p-3 border border-input rounded-md"
                rows={3}
                placeholder="Tell us about your business..."
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-cape-red hover:bg-red-800 text-white py-3"
            >
              {loading ? 'Submitting...' : 'Submit Registration'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-[#8C8885] text-sm">
              Already have an account?{' '}
              <Link to="/wholesale/login" className="text-cape-red hover:underline">
                Login here
              </Link>
            </p>
          </div>
        </div>

        <div className="mt-8 text-center">
          <Link to="/" className="text-[#8C8885] text-sm hover:underline">
            ← Back to retail store
          </Link>
        </div>
      </div>
    </div>
  );
};

export default WholesaleRegisterPage;
